import { motion } from "framer-motion";
import { Bot, MessageSquare, Package, Plus, Radio, ShoppingBag } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLanguageStore } from "@/stores/languageStore";

interface FlowStep {
  icon: LucideIcon;
  title: string;
  titleAm: string;
  body: string;
  bodyAm: string;
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

const FLOW_STEPS: FlowStep[] = [
  {
    icon: Package,
    title: "Add product once",
    titleAm: "ምርትን አንዴ ያክሉ",
    body: "Save name, price, category, and stock. This becomes your shared catalog.",
    bodyAm: "ስም፣ ዋጋ፣ ምድብና ክምችት ያስቀምጡ። ይህ የጋራ የምርት ዝርዝርዎ ይሆናል።",
  },
  {
    icon: Bot,
    title: "Bot uses the catalog",
    titleAm: "ቦቱ ዝርዝሩን ይጠቀማል",
    body: "Customers can ask about products, price, and availability from Telegram.",
    bodyAm: "ደንበኞች በቴሌግራም ስለ ምርት፣ ዋጋና መኖር መጠየቅ ይችላሉ።",
  },
  {
    icon: Radio,
    title: "Channels promote products",
    titleAm: "ቻናሎች ምርቶችን ያስተዋውቃሉ",
    body: "After the bot is channel admin, SARTN helps write and schedule product posts.",
    bodyAm: "ቦቱ የቻናል አስተዳዳሪ ከሆነ በኋላ SARTN የምርት ፖስት ይጽፋልና ያቅዳል።",
  },
  {
    icon: MessageSquare,
    title: "Groups handle questions",
    titleAm: "ቡድኖች ጥያቄዎችን ይይዛሉ",
    body: "In groups, SARTN can answer common product questions and moderate conversations.",
    bodyAm: "በቡድኖች ውስጥ SARTN የተደጋጋሚ የምርት ጥያቄዎችን ይመልሳልና ውይይቶችን ይቆጣጠራል።",
  },
];

export function BusinessOnboarding() {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-3xl bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500">
          <ShoppingBag className="h-6 w-6" />
        </div>
        <h2 className="font-headline text-xl font-bold tracking-tight text-[var(--color-text)]">
          {localize("Set up your Telegram store", "የቴሌግራም ሱቅዎን ያዘጋጁ", language)}
        </h2>
        <p className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
          {localize(
            "Start with one product. SARTN uses the same catalog in the bot, your channel posts, and group replies.",
            "በአንድ ምርት ይጀምሩ። SARTN ያንኑ የምርት ዝርዝር በቦት፣ በቻናል ፖስቶችና በቡድን መልሶች ይጠቀማል።",
            language,
          )}
        </p>
        <button
          type="button"
          onClick={() => { window.location.hash = "/business/add-product"; }}
          className="mt-5 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-semibold text-white active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" />
          {localize("Add first product", "የመጀመሪያ ምርት አክል", language)}
        </button>
      </section>

      <section className="grid gap-3">
        {FLOW_STEPS.map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.article
              key={step.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04, duration: 0.2 }}
              className="flex gap-3 rounded-2xl bg-[var(--color-surface)] p-4"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--color-bg)] text-[var(--color-primary)]">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-text)]">
                  {localize(step.title, step.titleAm, language)}
                </h3>
                <p className="mt-1 text-xs leading-5 text-[var(--color-text-secondary)]">
                  {localize(step.body, step.bodyAm, language)}
                </p>
              </div>
            </motion.article>
          );
        })}
      </section>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => { window.location.hash = "/channels"; }}
          className="min-h-11 rounded-2xl bg-[var(--color-surface)] text-sm font-semibold text-[var(--color-text)] active:scale-[0.98]"
        >
          {localize("Connect channel", "ቻናል አገናኝ", language)}
        </button>
        <button
          type="button"
          onClick={() => { window.location.hash = "/groups"; }}
          className="min-h-11 rounded-2xl bg-[var(--color-surface)] text-sm font-semibold text-[var(--color-text)] active:scale-[0.98]"
        >
          {localize("Connect group", "ቡድን አገናኝ", language)}
        </button>
      </div>
    </div>
  );
}
